import { useCallback, useEffect, useRef, useState } from "react";
import { confirmAction } from "@/components/app/Confirm";
import { useQuery } from "@tanstack/react-query";
import Editor from "@monaco-editor/react";
import { Bold, Italic, List, Loader2, Paperclip, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { TaskPreview } from "@/pages/job/ws/TaskPreview";
import { cn } from "@/lib/utils";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function RichText({ initial, onChange, onPaste }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.innerHTML = initial || ""; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cmd = (c) => { document.execCommand(c); onChange(ref.current.innerHTML); };
  return (
    <div className="rounded-[6px] border border-line bg-white" data-testid="ws-rich-text">
      <div className="flex gap-1 border-b border-line p-1.5">{[[Bold, "bold"], [Italic, "italic"], [List, "insertUnorderedList"]].map(([I, c]) => <button key={c} type="button" onMouseDown={(e) => { e.preventDefault(); cmd(c); }} className="rounded p-1.5 text-ink-secondary hover:bg-subtle"><I size={14} /></button>)}</div>
      <div ref={ref} contentEditable suppressContentEditableWarning className="prose-sm min-h-[260px] p-4 text-sm outline-none" onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onPaste={(e) => onPaste((e.clipboardData.getData("text") || "").length)} data-testid="ws-text-input" />
    </div>
  );
}

export function WsWorkspace({ token, types, onDone }) {
  const { data } = useQuery({ queryKey: ["ws-workspace", token], queryFn: () => get(`/public/work-sample/${token}/workspace`), staleTime: Infinity, retry: false });
  const [left, setLeft] = useState(null);
  const [links, setLinks] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const draft = useRef({});
  const pastes = useRef([]);
  const tabs = useRef(0);
  const warned = useRef(false);
  const doneRef = useRef(false);

  useEffect(() => { if (data) { setLeft(data.remaining_seconds); setLinks(data.answer.links.length ? data.answer.links : [""]); setFiles(data.answer.files); } }, [data]);
  const save = useCallback(async () => {
    const body = { ...draft.current, paste_events: pastes.current, tab_switches: tabs.current };
    if (!Object.keys(draft.current).length && !pastes.current.length && !tabs.current) return;
    draft.current = {}; pastes.current = []; tabs.current = 0;
    try { const { data: r } = await api.post(`/public/work-sample/${token}/save`, body); setLeft(r.remaining_seconds); }
    catch (e) { if (e.response?.status === 409) { doneRef.current = true; onDone(); } }
  }, [token, onDone]);
  const submit = useCallback(async (auto) => {
    if (doneRef.current) return;
    setBusy(true);
    await save();
    try { await api.post(`/public/work-sample/${token}/submit`); } catch (e) { if (e.response?.status !== 409) { toast.error(errMsg(e)); setBusy(false); return; } }
    doneRef.current = true;
    if (auto) toast.info("Time's up — your work was submitted automatically.");
    onDone();
  }, [save, token, onDone]);

  useEffect(() => { const t = setInterval(save, 15000); const v = () => { if (document.visibilityState === "hidden") { tabs.current += 1; save(); } }; document.addEventListener("visibilitychange", v); return () => { clearInterval(t); document.removeEventListener("visibilitychange", v); }; }, [save]);
  useEffect(() => {
    if (left == null) return undefined;
    if (left <= 0) { submit(true); return undefined; }
    if (left <= 300 && !warned.current) { warned.current = true; toast.warning("5 minutes left — your work will be submitted automatically at time-up."); }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, submit]);

  if (!data || left == null) return <Skel className="h-96" />;
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Files must be 25 MB or smaller."); return; }
    const fd = new FormData(); fd.append("file", f);
    try { const { data: r } = await api.post(`/public/work-sample/${token}/files`, fd); setFiles((x) => [...x, r]); } catch (e) { toast.error(errMsg(e)); }
  };
  const setLink = (i, v) => { const n = links.map((l, k) => (k === i ? v : l)); setLinks(n); draft.current.links = n.filter(Boolean); };

  return (
    <div className="space-y-5" data-testid="ws-workspace">
      <div className={cn("sticky top-0 z-10 -mx-4 flex items-center justify-between bg-canvas/95 px-4 py-3 sm:mx-0 sm:px-0", left <= 300 && "text-risk")}>
        <span className="text-sm" data-testid="ws-autosave-note">Autosaves every 15 seconds</span>
        <span className="font-display text-2xl font-semibold tabular-nums" data-testid="ws-timer">{fmt(Math.max(0, left))}</span>
      </div>
      {left <= 300 && <p className="rounded-lg border border-[#E8C9C5] bg-risk-tint px-3 py-2 text-sm text-risk" data-testid="ws-time-warning">Less than 5 minutes left. Your work will be submitted automatically.</p>}
      <div className="panel p-5 sm:p-6"><TaskPreview v={data.variant} criteria={data.criteria} /></div>
      {types.includes("text") && <RichText initial={data.answer.text_html} onChange={(h) => { draft.current.text_html = h; }} onPaste={(n) => pastes.current.push(n)} />}
      {types.includes("code") && (
        <div className="overflow-hidden rounded-[6px] border border-line" data-testid="ws-code-editor">
          <Editor height="320px" defaultLanguage={data.answer.code_language || "python"} defaultValue={data.answer.code} onChange={(v) => { draft.current.code = v || ""; }}
            onMount={(ed) => ed.onDidPaste((e) => pastes.current.push(ed.getModel().getValueInRange(e.range).length))} options={{ minimap: { enabled: false }, fontSize: 13 }} />
        </div>
      )}
      {types.includes("file") && (
        <div className="panel space-y-2 p-4" data-testid="ws-files">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-brand"><Paperclip size={14} /> Attach a file (PDF, DOCX, XLSX, PPTX or ZIP, up to 25 MB)
            <input type="file" accept=".pdf,.docx,.xlsx,.pptx,.zip" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="ws-file-input" /></label>
          {files.map((f) => <p key={f.id} className="flex items-center gap-2 text-sm">{f.name} <span className="text-xs text-ink-muted">{Math.round(f.size / 1024)} KB</span>
            <button aria-label={`Remove ${f.name}`} onClick={async () => { if (!(await confirmAction({ title: "Remove this file?", entity: f.name, confirmLabel: "Remove" }))) return; await api.delete(`/public/work-sample/${token}/files/${f.id}`); setFiles((x) => x.filter((y) => y.id !== f.id)); }}><Trash2 size={13} className="text-ink-muted" /></button></p>)}
        </div>
      )}
      {types.includes("link") && (
        <div className="panel space-y-2 p-4" data-testid="ws-links">{links.map((l, i) => <Input key={i} value={l} placeholder="https://" onChange={(e) => setLink(i, e.target.value)} data-testid={`ws-link-${i}`} />)}
          {links.length < 5 && <Button size="sm" variant="ghost" onClick={() => setLinks([...links, ""])}><Plus size={14} /> Add link</Button>}</div>
      )}
      <Button className="h-12 w-full text-base sm:w-auto sm:px-10" disabled={busy} onClick={async () => (await confirmAction({ title: "Submit your work now?", body: "You can't change it afterwards.", confirmLabel: "Submit" })) && submit(false)} data-testid="ws-submit-btn">
        {busy && <Loader2 size={16} className="animate-spin" />} Submit my work
      </Button>
    </div>
  );
}
